/**
 * UI component schema v1 — the browser side of the Runtime's
 * `components/ui_components` builders (LLD "Next-Action Recommendation" §3.8).
 *
 * A component is one JSON object `{type, version, id, title, props, data, actions}`:
 * `props` says how to render / interact, `data` what to render, `actions` what a
 * user interaction does. The same object is streamed live and stored in history,
 * so live and history render through the same parser and renderer.
 *
 * Parsers never throw: anything this build cannot render faithfully (unknown
 * type or version, a prop combination the renderer does not implement, malformed
 * fields) returns null and the caller shows nothing.
 */

export const COMPONENT_SCHEMA_VERSION = 1;

const APPEARANCES = ['checkbox', 'radio', 'chip', 'button'] as const;
export type ChoiceAppearance = (typeof APPEARANCES)[number];

const OPTIONS_MAX = 10;

export interface ChoiceOption {
  id: string;
  /** What is shown. Plain text — never rendered as HTML or markdown. */
  label: string;
  /** What choosing this option sends. */
  value: string;
}

/**
 * `choice_group`, limited to what the renderer implements: single selection,
 * submitted on select. `multiple` / `on_confirm` are valid in the schema but are
 * rejected here until a renderer for them exists.
 */
export interface ChoiceGroupComponent {
  type: 'choice_group';
  version: 1;
  id: string;
  title: string | null;
  props: {
    appearance: ChoiceAppearance;
    selection: 'single';
    submit: 'on_select';
  };
  data: { options: ChoiceOption[] };
  actions: { on_submit: { type: 'send_message' } };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Accepts the component as an object or as its JSON string (history rows). */
function asObject(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    if (!raw.trim()) return null;
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return isRecord(raw) ? raw : null;
}

function parseOptions(raw: unknown): ChoiceOption[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > OPTIONS_MAX) return null;
  const seen = new Set<string>();
  const options: ChoiceOption[] = [];
  for (const o of raw) {
    if (!isRecord(o) || !nonEmptyString(o.id) || !nonEmptyString(o.label) || !nonEmptyString(o.value)) {
      return null;
    }
    if (seen.has(o.id)) return null;
    seen.add(o.id);
    options.push({ id: o.id, label: o.label, value: o.value });
  }
  return options;
}

/** Returns a normalized copy holding only known fields, or null. */
export function parseChoiceGroup(raw: unknown): ChoiceGroupComponent | null {
  const c = asObject(raw);
  if (!c || c.type !== 'choice_group' || c.version !== COMPONENT_SCHEMA_VERSION) return null;
  if (!nonEmptyString(c.id)) return null;
  if (c.title !== null && c.title !== undefined && typeof c.title !== 'string') return null;

  const props = c.props;
  if (!isRecord(props)) return null;
  const appearance = props.appearance;
  if (typeof appearance !== 'string' || !(APPEARANCES as readonly string[]).includes(appearance)) return null;
  if (props.selection !== 'single' || props.submit !== 'on_select') return null;

  if (!isRecord(c.data)) return null;
  const options = parseOptions(c.data.options);
  if (!options) return null;

  const onSubmit = isRecord(c.actions) ? c.actions.on_submit : undefined;
  if (!isRecord(onSubmit) || onSubmit.type !== 'send_message') return null;

  return {
    type: 'choice_group',
    version: 1,
    id: c.id,
    title: nonEmptyString(c.title) ? c.title : null,
    props: { appearance: appearance as ChoiceAppearance, selection: 'single', submit: 'on_select' },
    data: { options },
    actions: { on_submit: { type: 'send_message' } },
  };
}
